import { useState } from 'react';
import type { Snapshot } from '../lib/stats';
import { busiestDay, dailyOutcomes, type DayOutcome } from '../lib/stats';
import { durationPhrase, successRatePhrase, workerTotals, workerWorkload, type WorkerRunTotals } from '../lib/finished';
import type { FinishedRun, Worker } from '../lib/data';

/** The compact line of measured numbers the operations desk carries. */
export function SnapshotStrip({ stats }: { stats: Snapshot }) {
  const tiles: readonly { label: string; value: number; tone?: 'ok' | 'bad' }[] = [
    { label: 'Running now', value: stats.runningNow },
    { label: 'Finished today', value: stats.finishedToday },
    { label: 'Failed today', value: stats.failedToday, tone: stats.failedToday > 0 ? 'bad' : undefined },
    { label: 'Workers working', value: stats.workersWorking },
    { label: 'Workers free', value: stats.workersFree },
    { label: 'Waiting on you', value: stats.waitingOnOwner, tone: stats.waitingOnOwner > 0 ? 'bad' : undefined },
  ];
  return (
    <ul className="tiles">
      {tiles.map((tile) => (
        <li key={tile.label} className={tile.tone ? `tile tile-${tile.tone}` : 'tile'}>
          <span className="tile-value">{tile.value}</span>
          <span className="tile-label">{tile.label}</span>
        </li>
      ))}
    </ul>
  );
}

const CHART_WIDTH = 640;
const CHART_HEIGHT = 150;
const GUTTER = 6;

/** The bars themselves, given the days to draw. Nothing here decides which days those are. */
function Bars({ rows, label }: { rows: readonly DayOutcome[]; label: string }) {
  const tallest = busiestDay(rows);
  const slot = CHART_WIDTH / rows.length;
  const barWidth = Math.max(4, slot - GUTTER);
  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} role="img" aria-label={label}>
        {rows.map((row, index) => <DayBar key={row.dayStartMs} row={row} index={index} slot={slot} barWidth={barWidth} tallest={tallest} />)}
      </svg>
      <figcaption className="row spread">
        <small className="muted">{rows[0]!.label} to {rows[rows.length - 1]!.label} · busiest day {tallest}</small>
        <small className="muted"><span className="key key-ok" aria-hidden="true" /> succeeded <span className="key key-bad" aria-hidden="true" /> failed</small>
      </figcaption>
    </figure>
  );
}

/**
 * Runs per day, succeeded under failed, drawn from the finished runs Command
 * holds. Bars only, no scale invented: the tallest day sets the height and the
 * count is written beside it, so nothing here is a proportion of a number
 * Command cannot see.
 */
export function WorkloadChart({ results, nowMs, days = 14 }: { results: readonly FinishedRun[]; nowMs: number; days?: number }) {
  if (results.length === 0) return <p className="muted">Finished runs are counted here once there are some.</p>;
  const rows = dailyOutcomes(results, nowMs, days);
  return <Bars rows={rows} label={`Runs a day over the last ${days} days, the busiest day being ${busiestDay(rows)}`} />;
}

/**
 * The same chart and the same counting, one worker at a time, so the owner can
 * see who carried the week and who did not. Every worker is listed, including
 * one that has finished nothing; runs whose worker is no longer recorded are
 * gathered at the end rather than dropped.
 */
export function WorkerWorkload({ results, workers, nowMs, days = 14 }: {
  results: readonly FinishedRun[]; workers: readonly Worker[]; nowMs: number; days?: number;
}) {
  const totals = workerTotals(results, workers);
  const [chosen, setChosen] = useState<string | null>(null);
  if (totals.length === 0) return <p className="muted">Per-worker figures appear here once there are workers.</p>;
  const keyOf = (row: WorkerRunTotals) => row.workerId ?? '';
  const fallback = totals.find((row) => row.runs > 0) ?? totals[0]!;
  const selected = totals.find((row) => keyOf(row) === chosen) ?? fallback;
  return (
    <div className="stack">
      <label className="row">
        <span className="muted">Worker</span>
        <select aria-label="Worker" value={keyOf(selected)} onChange={(e) => setChosen(e.target.value)}>
          {totals.map((row) => <option key={keyOf(row)} value={keyOf(row)}>{row.label} ({row.runs})</option>)}
        </select>
      </label>
      <WorkerTotalsLine row={selected} />
      {selected.runs === 0
        ? <p className="muted">{selected.label} has finished nothing Command can see.</p>
        : <Bars rows={workerWorkload(results, selected.workerId, nowMs, days)}
            label={`Runs a day for ${selected.label} over the last ${days} days`} />}
      <ul className="tiles">
        {totals.map((row) => (
          <li key={keyOf(row)} className="tile">
            <span className="tile-value">{row.runs}</span>
            <span className="tile-label">{row.label} · {successRatePhrase(row.successRate)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One worker's figures in a line: what they finished, how it went, how long it takes them. */
function WorkerTotalsLine({ row }: { row: WorkerRunTotals }) {
  return (
    <small className="muted">
      {row.runs} finished · {row.successes} succeeded · {row.failures} failed · {successRatePhrase(row.successRate)} succeeded
      {' · typically '}{durationPhrase(row.medianMs)}{' · on average '}{durationPhrase(row.averageMs)}
    </small>
  );
}

function DayBar({ row, index, slot, barWidth, tallest }: {
  row: DayOutcome; index: number; slot: number; barWidth: number; tallest: number;
}) {
  const x = index * slot + (slot - barWidth) / 2;
  const full = (row.total / tallest) * CHART_HEIGHT;
  const failed = row.total === 0 ? 0 : (row.failed / row.total) * full;
  const done = full - failed;
  return (
    <g>
      <title>{`${row.label}: ${row.done} succeeded, ${row.failed} failed`}</title>
      <rect className="bar-empty" x={x} y={CHART_HEIGHT - 2} width={barWidth} height={2} />
      <rect className="bar-bad" x={x} y={CHART_HEIGHT - failed} width={barWidth} height={failed} />
      <rect className="bar-ok" x={x} y={CHART_HEIGHT - full} width={barWidth} height={done} />
    </g>
  );
}
